// frontend/src/hooks/useUserTimezone.js - Hook for smart user timezone management
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import {
    getUserEffectiveTimezone,
    refreshUserTimezone,
    formatSmartDateTime,
    formatSmartDateTimeSync
} from '../utils/formatters';

export const useUserTimezone = () => {
    const { user } = useAuth();
    const [effectiveTimezone, setEffectiveTimezone] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Determinar zona horaria efectiva del usuario
    const determineTimezone = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const userTimezone = user?.timezone;
            const timezone = await getUserEffectiveTimezone(userTimezone);
            setEffectiveTimezone(timezone);
        } catch (err) {
            console.error('❌ useUserTimezone: Error determining timezone:', err);
            setError(err.message);

            // Fallback a zona horaria del navegador
            try {
                const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                setEffectiveTimezone(browserTimezone);
            } catch (fallbackError) {
                console.error('❌ useUserTimezone: Even browser timezone failed:', fallbackError);
                setEffectiveTimezone('America/Mexico_City'); // Último recurso
            }
        } finally {
            setIsLoading(false);
        }
    }, [user?.timezone, user?.id]);

    // Efecto para determinar zona horaria cuando cambia el usuario
    useEffect(() => {
        if (user) {
            determineTimezone();
        } else {
            // Si no hay usuario, usar zona horaria del navegador
            try {
                const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                setEffectiveTimezone(browserTimezone);
                setIsLoading(false);
            } catch (error) {
                setEffectiveTimezone('America/Mexico_City');
                setIsLoading(false);
            }
        }
    }, [determineTimezone, user]);

    // Función para refrescar zona horaria (llamar cuando se actualiza el perfil)
    const refreshTimezone = useCallback(async () => {
        refreshUserTimezone(); // Limpiar cache
        await determineTimezone(); // Re-determinar
    }, [determineTimezone]);

    // Función para formatear fecha con la zona horaria del usuario
    const formatUserDateTime = useCallback(async (timestamp, options = {}) => {
        if (!effectiveTimezone) {
            return timestamp?.toString() || '';
        }

        return await formatSmartDateTime(timestamp, user?.timezone, options);
    }, [effectiveTimezone, user?.timezone]);

    // Función síncrona para formatear (usa cache)
    const formatUserDateTimeSync = useCallback((timestamp, options = {}) => {
        if (!effectiveTimezone) {
            return timestamp?.toString() || '';
        }

        return formatSmartDateTimeSync(timestamp, effectiveTimezone, options);
    }, [effectiveTimezone]);

    // Función para obtener información de la zona horaria
    const getTimezoneInfo = useCallback(() => {
        if (!effectiveTimezone) return null;

        try {
            const now = new Date();
            const formatter = new Intl.DateTimeFormat('en', {
                timeZone: effectiveTimezone,
                timeZoneName: 'long'
            });

            const parts = formatter.formatToParts(now);
            const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value;

            // Calcular offset
            const utc = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
            const target = new Date(utc.toLocaleString('en-US', { timeZone: effectiveTimezone }));
            const offset = (target.getTime() - utc.getTime()) / (1000 * 60 * 60);

            return {
                timezone: effectiveTimezone,
                name: timeZoneName,
                offset: offset,
                offsetString: `GMT${offset >= 0 ? '+' : ''}${offset}`,
                isUserConfigured: Boolean(user?.timezone && effectiveTimezone === user.timezone),
                isBrowserDetected: !user?.timezone || effectiveTimezone !== user.timezone
            };
        } catch (error) {
            console.error('❌ useUserTimezone: Error getting timezone info:', error);
            return {
                timezone: effectiveTimezone,
                name: effectiveTimezone,
                offset: 0,
                offsetString: 'GMT+0',
                isUserConfigured: false,
                isBrowserDetected: true
            };
        }
    }, [effectiveTimezone, user?.timezone]);

    return {
        // Estado
        effectiveTimezone,
        isLoading,
        error,

        // Información
        timezoneInfo: getTimezoneInfo(),
        isUserConfigured: Boolean(user?.timezone && effectiveTimezone === user.timezone),
        isBrowserDetected: !user?.timezone || effectiveTimezone !== user.timezone,

        // Funciones
        refreshTimezone,
        formatUserDateTime,
        formatUserDateTimeSync,
        getTimezoneInfo,

        // Utilidades
        hasUserTimezone: Boolean(user?.timezone),
        userTimezone: user?.timezone,
        browserTimezone: (() => {
            try {
                return Intl.DateTimeFormat().resolvedOptions().timeZone;
            } catch {
                return 'America/Mexico_City';
            }
        })()
    };
};

export default useUserTimezone;